import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Grid, Icon } from '@mui/material';
import PropTypes from 'prop-types';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftBadge from 'components/SoftBadge';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import { partnerService } from 'services/partnerService';
import iaasService from 'services/iaasService';
import saasService from 'services/saasService';
import ServiceAssignDialog from './ServiceAssignDialog';
import { AuthContext } from 'context';

const CustomerServices = ({ serviceType }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [ownServices, setOwnServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState('customer'); // 'customer' oder 'own'
  const [editingPrice, setEditingPrice] = useState({ serviceId: null, value: '' });
  
  useEffect(() => {
    const fetchServices = async () => {
      setIsLoading(true);
      try {
        // Hole Kunden-Services
        const customerServicesData = await partnerService.getCustomerServices(serviceType);
        setServices(customerServicesData);
        
        // Hole auch eigene Services, die zugewiesen werden können
        let ownServicesData = [];
        if (serviceType === 'iaas') {
          const response = await iaasService.getUserServices();
          ownServicesData = response.data.filter(service =>
            !service.partnerId || service.partnerId === user?.id
          );
        } else {
          const response = await saasService.getUserServices();
          ownServicesData = response.data.filter(service =>
            !service.partnerId || service.partnerId === user?.id
          );
        }
        setOwnServices(ownServicesData);
      } catch (error) {
        console.error(`Fehler beim Laden der ${serviceType}-Services:`, error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchServices();
  }, [serviceType]);
  
  const handleAssignService = (service) => {
    setSelectedService(service);
    setAssignDialogOpen(true);
  };
  
  const handleSaveEndCustomerPrice = async (service) => {
    try {
      const price = editingPrice.value;
      if (serviceType === 'iaas') {
        await iaasService.updateEndCustomerPrice(service.id, price);
      } else {
        await saasService.updateEndCustomerPrice(service.id, price);
      }
      setServices(prevServices => prevServices.map(s =>
        s.id === service.id
          ? { ...s, endcustomer_price: price !== '' ? parseFloat(price) : null }
          : s
      ));
      setEditingPrice({ serviceId: null, value: '' });
    } catch (error) {
      console.error('Fehler beim Speichern des Endkundenpreises:', error);
    }
  };

  const handleAssignComplete = (serviceData) => {
    // Update die Listen nach der Zuweisung
    setAssignDialogOpen(false);
    
    // Entferne den zugewiesenen Service aus der eigenen Liste
    setOwnServices(prevServices => prevServices.filter(s => s.id !== selectedService.id));
    
    // Lade die Kunden-Services neu
    partnerService.getCustomerServices(serviceType)
      .then(customerServicesData => {
        setServices(customerServicesData);
      })
      .catch(error => {
        console.error(`Fehler beim Neuladen der Kunden-Services:`, error);
      });
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'suspended':
        return 'error';
      default:
        return 'dark';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'pending':
        return 'In Bearbeitung';
      case 'suspended':
        return 'Ausgesetzt';
      default:
        return status;
    }
  };
  
  const getServiceIcon = (type) => {
    if (serviceType === 'iaas') {
      switch (type) {
        case 'windows-server':
          return 'computer';
        case 'linux-server':
          return 'terminal';
        case 'private-cloud':
          return 'cloud';
        case 'private-network':
          return 'router';
        case 's3-storage':
          return 'storage';
        default:
          return 'memory';
      }
    } else {
      // SaaS Icons
      switch (type) {
        case 'office365':
        case 'office-365':
          return 'article';
        case 'email':
          return 'email';
        case 'backup':
        case 'backup-now':
          return 'backup';
        case 'security':
        case 'eset':
          return 'security';
        default:
          return 'apps';
      }
    }
  };
  
  const displayedServices = activeTab === 'customer' ? services : ownServices;
  
  const filteredServices = displayedServices.filter(
    (service) => {
      const searchTermLower = searchTerm.toLowerCase();
      return service.servicename.toLowerCase().includes(searchTermLower) || 
             (service.user?.company && service.user.company.toLowerCase().includes(searchTermLower)) || 
             service.hostname.toLowerCase().includes(searchTermLower);
    }
  );

  return (
    <SoftBox>
      <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <SoftTypography variant="h5" fontWeight="medium">
          {serviceType === 'iaas' ? 'IaaS' : 'SaaS'} Services
        </SoftTypography>
        <SoftBox display="flex" alignItems="center" gap={2}>
          <SoftButton 
            variant={activeTab === 'customer' ? 'gradient' : 'outlined'} 
            color="info"
            size="small"
            onClick={() => setActiveTab('customer')}
          >
            Kunden-Services
          </SoftButton>
          <SoftButton 
            variant={activeTab === 'own' ? 'gradient' : 'outlined'} 
            color="info"
            size="small"
            onClick={() => setActiveTab('own')}
          >
            Eigene Services
          </SoftButton>
          <SoftBox width="200px">
            <SoftInput
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={{ component: "search", direction: "left" }}
            />
          </SoftBox>
        </SoftBox>
      </SoftBox>
      
      {isLoading ? (
        <SoftBox py={5} textAlign="center">
          <SoftTypography>Daten werden geladen...</SoftTypography>
        </SoftBox>
      ) : filteredServices.length > 0 ? (
        <Grid container spacing={3}>
          {filteredServices.map((service) => (
            <Grid item xs={12} md={6} lg={4} key={service.id}>
              <Card>
                <SoftBox p={3}>
                  <SoftBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <SoftBox display="flex" alignItems="center">
                      <SoftBox
                        width="40px"
                        height="40px"
                        bgColor={serviceType === 'iaas' ? 'info' : 'success'}
                        variant="gradient"
                        borderRadius="lg"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        mr={2}
                      >
                        <Icon fontSize="small" sx={{ color: 'white' }}>
                          {getServiceIcon(service.service_type || service.producttype)}
                        </Icon>
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="h6" fontWeight="medium">
                          {service.servicename}
                        </SoftTypography>
                        <SoftTypography variant="button" color="text">
                          {service.hostname}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    <SoftBadge
                      variant="contained"
                      color={getStatusColor(service.sstatus)}
                      size="xs"
                    >
                      {getStatusText(service.sstatus)}
                    </SoftBadge>
                  </SoftBox>
                  
                  {activeTab === 'customer' && (
                    <SoftBox mb={2}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        KUNDE
                      </SoftTypography>
                      <SoftTypography variant="button" fontWeight="regular" color="text" display="block">
                        {service.user ? service.user.company : 'Unbekannt'}
                      </SoftTypography>
                    </SoftBox>
                  )}
                  
                  {activeTab === 'customer' ? (
                    <SoftBox>
                      <SoftBox mb={0.5}>
                        <SoftTypography variant="caption" color="text" fontWeight="medium">Einkaufspreis: </SoftTypography>
                        <SoftTypography component="span" variant="caption" fontWeight="bold">
                          {parseFloat(service.price || service.sprice || 0).toFixed(2)} €/Monat
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center" mb={1.5} style={{ gap: 4, flexWrap: 'wrap' }}>
                        <SoftTypography variant="caption" color="text" fontWeight="medium">Endkundenpreis:</SoftTypography>
                        {editingPrice.serviceId === service.id ? (
                          <>
                            <SoftBox style={{ maxWidth: 90 }}>
                              <SoftInput
                                type="number"
                                size="small"
                                value={editingPrice.value}
                                onChange={(e) => setEditingPrice(prev => ({ ...prev, value: e.target.value }))}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </SoftBox>
                            <SoftTypography variant="caption">€</SoftTypography>
                            <SoftButton variant="gradient" color="success" size="small" onClick={() => handleSaveEndCustomerPrice(service)}>✓</SoftButton>
                            <SoftButton variant="outlined" color="secondary" size="small" onClick={() => setEditingPrice({ serviceId: null, value: '' })}>✗</SoftButton>
                          </>
                        ) : (
                          <>
                            <SoftTypography component="span" variant="caption" fontWeight="bold">
                              {service.endcustomer_price ? `${parseFloat(service.endcustomer_price).toFixed(2)} €/Monat` : '–'}
                            </SoftTypography>
                            <Icon
                              style={{ fontSize: 14, cursor: 'pointer', color: '#aaa', marginLeft: 2 }}
                              onClick={() => setEditingPrice({ serviceId: service.id, value: service.endcustomer_price ? parseFloat(service.endcustomer_price).toFixed(2) : '' })}
                            >
                              edit
                            </Icon>
                          </>
                        )}
                      </SoftBox>
                      <SoftButton
                        variant="gradient"
                        color={serviceType === 'iaas' ? 'info' : 'success'}
                        size="small"
                        onClick={() => navigate(`/${serviceType}/${serviceType === 'iaas' ? 'server/detail' : 'detail'}/${service.id}`)}
                      >
                        Service anzeigen
                      </SoftButton>
                    </SoftBox>
                  ) : (
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                      <SoftBox>
                        <SoftTypography component="span" variant="body2" fontWeight="bold">
                          {parseFloat(service.price || service.sprice || 0).toFixed(2)} €
                        </SoftTypography>
                        <SoftTypography component="span" variant="button" color="text">
                          {' / Monat'}
                        </SoftTypography>
                      </SoftBox>
                      <SoftButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => handleAssignService(service)}
                      >
                        Zuweisen
                      </SoftButton>
                    </SoftBox>
                  )}
                </SoftBox>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <SoftBox py={5} textAlign="center">
          <SoftTypography variant="body2" color="text">
            {activeTab === 'customer'
              ? `Keine ${serviceType === 'iaas' ? 'IaaS' : 'SaaS'} Services für Ihre Kunden gefunden`
              : `Keine eigenen ${serviceType === 'iaas' ? 'IaaS' : 'SaaS'} Services zum Zuweisen vorhanden. Bestellen Sie zunächst Services unter dem entsprechenden ${serviceType === 'iaas' ? 'IaaS' : 'SaaS'}-Bereich.`
            }
          </SoftTypography>
        </SoftBox>
      )}
      
      <ServiceAssignDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        serviceId={selectedService?.id}
        serviceType={serviceType}
        onAssigned={handleAssignComplete}
      />
    </SoftBox>
  );
};

CustomerServices.propTypes = {
  serviceType: PropTypes.oneOf(['iaas', 'saas']).isRequired,
};

export default CustomerServices;